import { useEffect, useState, useRef } from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { Content, Page, Progress } from '@backstage/core-components';
import { useLocation } from 'react-router-dom';
import { IconButton, Snackbar } from '@material-ui/core';
import ShareIcon from '@material-ui/icons/Share';
import { SentryIcon } from '../../lib/SentryIcon';
import { ArgoIcon } from '../../lib/ArgoIcon';

export const CorootPage = () => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const organizationName =
    configApi.getOptionalString('organization.name') ?? 'Backstage';
  const backendBaseUrl =
    configApi.getOptionalString('backend.baseUrl') ?? 'http://localhost:7007';
  const [cookieSet, setCookieSet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Cookie renewal interval in milliseconds (e.g., every 10 minutes)
  const COOKIE_RENEWAL_INTERVAL = 5 * 60 * 1000;

  const location = useLocation();

  // Extract the subpath from the URL (everything after /coroot)
  let subPath = '';
  if (location.pathname.startsWith('/coroot')) {
    subPath = location.pathname.substring('/coroot'.length);
  }

  // Construct the iframe URL with the subpath and query parameters
  // `${backendBaseUrl}/api/coroot-proxy${subPath}${location.search}`
  const iframeSrc = `${backendBaseUrl}/api/coroot-proxy${subPath}${location.search}`;

  // Set up initial cookie fetch and periodic renewal
  useEffect(() => {
    const fetchCookie = async () => {
      try {
        if (!cookieSet) {
          setIsLoading(true);
        }

        const response = await fetchApi.fetch(
          `${backendBaseUrl}/api/coroot-proxy/cookie`,
        );

        if (response.ok) {
          setCookieSet(true);
        }
      } catch (error) {
        /* empty */
      } finally {
        setIsLoading(false);
      }
    };

    fetchCookie();

    const intervalId = setInterval(fetchCookie, COOKIE_RENEWAL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [backendBaseUrl, fetchApi, COOKIE_RENEWAL_INTERVAL, cookieSet]);

  // Function to open the corresponding Sentry link in a new tab
  const openInSentry = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const iframeUrl = iframeRef.current.contentWindow.location.href;

      // Extract application name using regex (looking for pattern after "Deployment:")
      const match = iframeUrl.match(/Deployment:([^/]+)/);
      if (match && match[1]) {
        const applicationName = match[1];

        // Construct Sentry URL with the application name
        const sentryUrl = `https://sentry.enspire.danfoss.com/organizations/alsense/issues/?project=-1&query=server_name%3A${applicationName}%2A&referrer=issue-list&statsPeriod=7d`;

        // Open in new tab
        window.open(sentryUrl, '_blank');
      } else {
        // Show the banner when no application name is found
        setShowBanner(true);
      }
    } else {
      // Show the banner when iframe reference is not available
      setShowBanner(true);
    }
  };

  const openInArgo = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const iframeUrl = iframeRef.current.contentWindow.location.href;

      // Extract application name using regex (looking for pattern after "Deployment:")
      const match = iframeUrl.match(/Deployment:([^/]+)/);
      if (match && match[1]) {
        const applicationName = match[1];

        // Construct Sentry URL with the application name
        const sentryUrl = `https://argocd.alsenseplatform.com/applications?search=${applicationName}`;

        // Open in new tab
        window.open(sentryUrl, '_blank');
      } else {
        // Show the banner when no application name is found
        setShowBanner(true);
      }
    } else {
      // Show the banner when iframe reference is not available
      setShowBanner(true);
    }
  };

  const copyHrefToClipboard = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const iframeUrl = iframeRef.current.contentWindow.location.href;

      // Rewrite the URL to use the FE URL
      const rewrittenUrl = iframeUrl.replace('/api/coroot-proxy', '/coroot');

      // Copy the rewritten URL to the clipboard
      navigator.clipboard.writeText(rewrittenUrl).then(() => {
        // Show the success banner
        setShowSuccessBanner(true);
      });
    } else {
      // Show the banner when iframe reference is not available
      setShowBanner(true);
    }
  };

  const handleCloseSuccessBanner = (
    _event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSuccessBanner(false);
  };

  const handleCloseBanner = (
    _event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowBanner(false);
  };

  return (
    <Page themeId="home">
      <Content>
        <Snackbar
          open={showSuccessBanner}
          autoHideDuration={5000}
          onClose={handleCloseSuccessBanner}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          message="URL successfully copied to clipboard!"
        />
        <Snackbar
          open={showBanner}
          autoHideDuration={5000}
          onClose={handleCloseBanner}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          message="App name could not be found. Open an Application in Coroot."
        />
        {isLoading ? (
          <Progress />
        ) : (
          <div>
            <h1>{`${organizationName} Root Cause Analysis`}</h1>
            <iframe
              ref={iframeRef}
              title={`${organizationName} Root Cause Analysis`}
              src={cookieSet ? iframeSrc : ''}
              style={{
                width: '100%',
                height: '1200px',
                border: 'none',
                borderRadius: '8px',
              }}
            />
          </div>
        )}
        <IconButton
          onClick={openInSentry}
          style={{
            position: 'fixed',
            bottom: '50px',
            right: '50px',
            border: '1px solid #fff',
            backgroundColor: '#321b3b',
          }}
          aria-label="Open in Sentry"
        >
          <SentryIcon />
        </IconButton>
        <IconButton
          onClick={openInArgo}
          style={{
            position: 'fixed',
            bottom: '110px',
            right: '50px',
            border: '1px solid #fff',
            backgroundColor: '#0f2733',
          }}
          aria-label="Open in ArgoCD"
        >
          <ArgoIcon />
        </IconButton>
        <IconButton
          onClick={copyHrefToClipboard}
          style={{
            position: 'fixed',
            bottom: '170px',
            right: '50px',
            border: '1px solid #fff',
            backgroundColor: '#303030',
          }}
          aria-label="Share with others"
        >
          <ShareIcon style={{ color: 'white' }} />
        </IconButton>
      </Content>
    </Page>
  );
};
